import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../lib/api';
import { trackEvent } from '../lib/analytics';

interface CallbackFormValues {
  name: string;
  phone: string;
  bestTime: string;
  honeypot: string;
}

/**
 * Floating "Request a Callback" button + modal.
 * Submits to /api/inquiries with type=callback. The Function persists the
 * inquiry to Storage Tables and forwards it to the configured Logic App.
 */
export default function CallbackButton() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CallbackFormValues>();

  const onSubmit = async (values: CallbackFormValues) => {
    if (values.honeypot) return;
    try {
      await api.createInquiry({
        type: 'callback',
        name: values.name,
        phone: values.phone,
        email: '',
        message: values.bestTime
          ? `Please call back. Best time: ${values.bestTime}`
          : 'Please call back at your earliest.',
        honeypot: values.honeypot,
      });
      trackEvent('inquiry_submit', { inquiry_type: 'callback' });
      setStatus('success');
      reset();
    } catch (e) {
      setStatus('error');
      setErrorMessage(e instanceof Error ? e.message : 'Submission failed');
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setStatus('idle'), 200);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Request a callback"
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-brand-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:bg-brand-600"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        Request a Callback
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4" onClick={close}>
          <div
            className="card w-full max-w-md p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-semibold">Request a callback</h3>
                <p className="mt-1 text-sm text-slate-600">Leave your number and we'll call you within 1 business day.</p>
              </div>
              <button onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-700">✕</button>
            </div>

            {status === 'success' ? (
              <div className="mt-6 rounded-xl border border-accent-green/30 bg-green-50 p-5 text-center">
                <div className="font-display text-lg font-semibold text-green-800">Thanks! We'll call you back.</div>
                <button onClick={close} className="btn-secondary mt-4">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="mt-5 grid gap-4">
                <input type="text" autoComplete="off" tabIndex={-1} aria-hidden className="hidden" {...register('honeypot')} />

                <div>
                  <label className="label">Your name *</label>
                  <input className="input" autoFocus {...register('name', { required: true, minLength: 2 })} />
                  {errors.name && <p className="mt-1 text-xs text-red-600">Required</p>}
                </div>
                <div>
                  <label className="label">Phone *</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    className="input"
                    placeholder="+91 9999900000"
                    {...register('phone', { required: true, minLength: 7 })}
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-600">Required</p>}
                </div>
                <div>
                  <label className="label">Best time to call</label>
                  <input
                    className="input"
                    placeholder="e.g. weekdays 10am – 6pm"
                    {...register('bestTime')}
                  />
                </div>

                {status === 'error' && (
                  <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage}</p>
                )}

                <button type="submit" disabled={isSubmitting} className="btn-primary mt-1">
                  {isSubmitting ? 'Submitting…' : 'Request Callback'}
                </button>
                <p className="text-center text-xs text-slate-500">
                  By submitting you agree to be contacted by Uclipse.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
